import type { ActionDefinition, ActionResult } from './widget-action.model';
import type { WidgetConfig, WidgetState } from './widget-config.model';

// Core contract that all widgets must implement. This interface enables AI controllers and external systems to interact with widgets in a uniform way.
export interface BaseWidget<Config extends WidgetConfig = WidgetConfig> {
  id: string; // e.g. 'quadratic-function'
  type: Config['type']; // e.g. 'function-plot'

  // Lifecycle methods
  init(config: Config): void; // Init widget with given config
  destroy(): void; // Clean up and destroy the widget
  update(config: Partial<Config>): void; // Update config dynamically

  // Action system - returns result with success flag and optional error message
  executeAction(action: ActionDefinition): Promise<ActionResult>;

  // State management
  getState(): WidgetState;
  setState(state: WidgetState): void;

  // Event system
  on(event: string, callback: (data: unknown) => void): void; // subscribe
  off(event: string, callback: (data: unknown) => void): void; // unsubscribe
}

// Event callback type for widget events.
export type WidgetEventCallback = (data: unknown) => void;

// Standard widget events that can be emitted.
export enum WidgetEvent {
  StateChange = 'stateChange',
  UserInteraction = 'userInteraction',
  AnimationComplete = 'animationComplete',
  Error = 'error',
  Ready = 'ready',
}
